import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import TrDateInput from '../components/TrDateInput';

// Ayarlar > Proje Tanımlamaları sekmesindeki sade proje listesi.
// NOT: Bu bileşen kasıtlı olarak Projeler.tsx (ana Proje Dashboard'u) ile
// bağımsızdır — burada sadece liste + yeni proje ekleme var, sekmeler/KPI
// kartları/bento tasarımı yok. Diğer tanımlama sayfalarıyla (Katlar, Cepheler,
// Manzaralar) aynı sade liste tasarım dilini kullanır.

interface Proje {
    projeID: number;
    projeAdi: string;
    ada: string | null;
    parsel: string | null;
    eklenmeTarihi: string;
    baslamaTarihi: string | null;
    bitisTarihi: string | null;
    projeLogo: string | null;
    takipNo: string;
    adres: string | null;
    sehir: string | null;
    kdv: number;
    projeDurumu: string;
    aktifMi: boolean;
}

interface ProjeForm {
    projeAdi: string;
    ada: string;
    parsel: string;
    baslamaTarihi: string;
    bitisTarihi: string;
    adres: string;
    sehir: string;
    kdv: number;
    projeDurumu: string;
}

const durumuMeta: Record<string, { label: string; color: string; dot: string }> = {
    'Devam Ediyor': { label: 'Devam Ediyor', color: '#b45309', dot: '#f59e0b' },
    'Tamamlandı': { label: 'Tamamlandı', color: '#15803d', dot: '#22c55e' },
    'Gelecek': { label: 'Gelecek', color: '#0369a1', dot: '#38bdf8' },
};
const getDurum = (d: string) => durumuMeta[d] ?? { label: d, color: '#475569', dot: '#94a3b8' };

const ProjeAyarlariListesi: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [formData, setFormData] = useState<ProjeForm>({
        projeAdi: '', ada: '', parsel: '',
        baslamaTarihi: '', bitisTarihi: '',
        adres: '', sehir: '', kdv: 20, projeDurumu: 'Gelecek'
    });

    const fetchProjeler = async () => {
        try {
            const response = await axiosInstance.get('/api/Projeler');
            setProjeler(response.data);
        } catch {
            toast.error(t('msg.projelerYuklemeHata'));
        }
    };

    useEffect(() => { fetchProjeler(); }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            let logoFileName = null;
            if (selectedFile) {
                const fd = new FormData();
                fd.append('file', selectedFile);
                fd.append('aciklama', 'Proje Logo');
                const up = await axiosInstance.post('/api/Dosyalar/upload', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                logoFileName = up.data.dosyaAdi;
            }
            await axiosInstance.post('/api/Projeler', {
                ...formData, projeLogo: logoFileName, aktifMi: true
            });
            toast.success(t('msg.projeEklendi'));
            handleCloseModal();
            fetchProjeler();
        } catch {
            toast.error(t('msg.projeEklenirkenHata'));
        } finally {
            setSaving(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type === 'image/jpeg' || file.type === 'image/png') {
            setSelectedFile(file);
        } else {
            toast.error(t('msg.sadecePngJpg'));
            e.target.value = '';
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedFile(null);
        setFormData({ projeAdi: '', ada: '', parsel: '', baslamaTarihi: '', bitisTarihi: '', adres: '', sehir: '', kdv: 20, projeDurumu: 'Gelecek' });
    };

    return (
        <>
            <style>{`
                .pal-overlay {
                    position: fixed; inset: 0;
                    background: rgba(15,23,42,0.45);
                    backdrop-filter: blur(3px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1050; padding: 20px;
                }
                .pal-modal {
                    background: #fff; border-radius: 18px;
                    width: 100%; max-width: 680px; max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 60px rgba(15,23,42,0.25);
                    animation: palModalIn 0.18s ease;
                }
                @keyframes palModalIn { from{opacity:0;transform:translateY(-14px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
                .pal-modal-header {
                    padding: 22px 26px 18px; border-bottom: 1px solid #f1f5f9;
                    display: flex; align-items: center; justify-content: space-between;
                    position: sticky; top: 0; background: #fff; z-index: 1;
                    border-radius: 18px 18px 0 0;
                }
                .pal-modal-title { font-size: 17px; font-weight: 700; color: #0f172a; margin: 0; }
                .pal-modal-close {
                    width: 30px; height: 30px; border-radius: 8px; border: none;
                    background: #f1f5f9; color: #64748b; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: background 0.15s;
                }
                .pal-modal-close:hover { background: #e2e8f0; }
                .pal-modal-body { padding: 22px 26px; }
                .pal-modal-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
                .pal-modal-full { grid-column: 1 / -1; }
                .pal-mfield { display: flex; flex-direction: column; gap: 6px; }
                .pal-mfield label {
                    font-size: 11.5px; font-weight: 600;
                    text-transform: uppercase; letter-spacing: 0.07em; color: #64748b;
                }
                .pal-mfield input, .pal-mfield select, .pal-mfield textarea {
                    padding: 10px 13px; border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 14px; font-family: inherit; color: #0f172a;
                    outline: none; background: #fff; box-sizing: border-box; width: 100%;
                    transition: border-color 0.15s, box-shadow 0.15s;
                }
                .pal-mfield input:focus, .pal-mfield select:focus, .pal-mfield textarea:focus {
                    border-color: #111827; box-shadow: 0 0 0 3px rgba(17,24,39,0.08);
                }
                .pal-mfield textarea { resize: vertical; min-height: 76px; line-height: 1.5; }
                .pal-mfield-hint { font-size: 11px; color: #94a3b8; margin-top: 4px; }
                .pal-modal-footer {
                    padding: 16px 26px; border-top: 1px solid #f1f5f9;
                    display: flex; justify-content: flex-end; gap: 10px;
                    position: sticky; bottom: 0; background: #fff;
                    border-radius: 0 0 18px 18px;
                }
                .pal-btn-cancel {
                    padding: 9px 18px; border-radius: 9px; border: 1.5px solid #e2e8f0;
                    background: #fff; color: #64748b;
                    font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
                    transition: background 0.15s;
                }
                .pal-btn-cancel:hover { background: #f1f5f9; }
                .pal-btn-save {
                    padding: 10px 20px; border-radius: 8px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .pal-btn-save:hover:not(:disabled) { background: #b85d05; transform: translateY(-1px); }
                .pal-btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
                .pal-spinner {
                    width: 13px; height: 13px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff; border-radius: 50%;
                    animation: palSpin .7s linear infinite;
                }
                @keyframes palSpin { to { transform: rotate(360deg); } }
                @media (max-width: 576px) { .pal-modal-grid { grid-template-columns: 1fr; } }
            `}</style>

            <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", padding: 24, background: '#fff' }}>
                <div style={{ paddingBottom: 20, marginBottom: 24, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 600, color: '#111827', margin: 0, lineHeight: 1.3 }}>{t('proje.baslik', 'Projeler')}</h1>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: '3px 0 0' }}>Proje listesi ve yönetimi</p>
                    </div>
                    <button
                        onClick={() => setShowModal(true)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                    >
                        <svg style={{ width: 14, height: 14, stroke: '#fff', strokeWidth: 2.5, fill: 'none' }} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Yeni Proje
                    </button>
                </div>

                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                    {projeler.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', color: '#9ca3af' }}>
                            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>🏢</div>
                            <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>{t('msg.henuzKayitEklenmedi', 'Henüz kayıt eklenmedi')}</p>
                        </div>
                    ) : (
                        projeler.map((proje, idx) => {
                            const durum = getDurum(proje.projeDurumu);
                            const subText = [proje.sehir, proje.adres].filter(Boolean).join(', ') || 'Konum belirtilmemiş';
                            return (
                                <div
                                    key={proje.projeID}
                                    onClick={() => navigate(`/projeler/${proje.projeID}`)}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: idx < projeler.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.13s', cursor: 'pointer' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                        <div style={{ width: 36, height: 36, background: '#f3f4f6', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#6b7280', flexShrink: 0 }}>
                                            {proje.projeAdi.slice(0, 2).toUpperCase()}
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <p style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{proje.projeAdi}</p>
                                            <p style={{ fontSize: 12, margin: '2px 0 0', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: durum.color, flexShrink: 0 }}>
                                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: durum.dot, display: 'inline-block' }} />
                                                    {durum.label}
                                                </span>
                                                · {subText}
                                            </p>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                                        <button
                                            title={t('btn.duzenle', 'Düzenle')}
                                            onClick={() => navigate(`/projeler/${proje.projeID}`)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#2563eb', display: 'flex', transition: 'background 0.13s' }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                        >
                                            <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' }} viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                        <button
                                            title={t('btn.sil', 'Sil')}
                                            onClick={() => toast.info(t('msg.onayGerekli', 'Bu işlem onay gerektiriyor'))}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#f43f5e', display: 'flex', transition: 'background 0.13s' }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#fff1f2')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                        >
                                            <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' }} viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {projeler.length > 0 && (
                    <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 12 }}>
                        Toplam <strong style={{ color: '#374151' }}>{projeler.length}</strong> proje
                    </p>
                )}
            </div>

            {showModal && (
                <div className="pal-overlay" onClick={handleCloseModal}>
                    <div className="pal-modal" onClick={e => e.stopPropagation()}>
                        <div className="pal-modal-header">
                            <h2 className="pal-modal-title">{t('proje.ekle', 'Yeni Proje Ekle')}</h2>
                            <button className="pal-modal-close" onClick={handleCloseModal} aria-label={t('btn.kapat', 'Kapat')}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="pal-modal-body">
                                <div className="pal-modal-grid">
                                    <div className="pal-mfield pal-modal-full">
                                        <label>{t('proje.projeAdi', 'Proje Adı')} <span style={{ color: '#e11d48' }}>*</span></label>
                                        <input
                                            type="text"
                                            value={formData.projeAdi}
                                            onChange={e => setFormData({ ...formData, projeAdi: e.target.value })}
                                            maxLength={50}
                                            required
                                            autoFocus
                                        />
                                    </div>
                                    <div className="pal-mfield">
                                        <label>{t('proje.sehir', 'Şehir')}</label>
                                        <input
                                            type="text"
                                            value={formData.sehir}
                                            onChange={e => setFormData({ ...formData, sehir: e.target.value })}
                                            maxLength={50}
                                        />
                                    </div>
                                    <div className="pal-mfield">
                                        <label>{t('proje.projeDurumu', 'Proje Durumu')}</label>
                                        <select value={formData.projeDurumu} onChange={e => setFormData({ ...formData, projeDurumu: e.target.value })}>
                                            <option value="Gelecek">Gelecek</option>
                                            <option value="Devam Ediyor">Devam Ediyor</option>
                                            <option value="Tamamlandı">{t('durum.tamamlandi', 'Tamamlandı')}</option>
                                        </select>
                                    </div>
                                    <div className="pal-mfield">
                                        <label>{t('proje.baslamaTarihi', 'Başlama Tarihi')}</label>
                                        <TrDateInput
                                            value={formData.baslamaTarihi}
                                            onChange={e => setFormData({ ...formData, baslamaTarihi: e.target.value })}
                                        />
                                    </div>
                                    <div className="pal-mfield">
                                        <label>{t('proje.bitisTarihi', 'Bitiş Tarihi')}</label>
                                        <TrDateInput
                                            value={formData.bitisTarihi}
                                            onChange={e => setFormData({ ...formData, bitisTarihi: e.target.value })}
                                        />
                                    </div>
                                    <div className="pal-mfield">
                                        <label>{t('label.ada', 'Ada')}</label>
                                        <input type="text" placeholder="Ada no" value={formData.ada} onChange={e => setFormData({ ...formData, ada: e.target.value })} maxLength={50} />
                                    </div>
                                    <div className="pal-mfield">
                                        <label>{t('label.parsel', 'Parsel')}</label>
                                        <input type="text" placeholder="Parsel no" value={formData.parsel} onChange={e => setFormData({ ...formData, parsel: e.target.value })} maxLength={50} />
                                    </div>
                                    <div className="pal-mfield">
                                        <label>{t('proje.kdv', 'KDV')}</label>
                                        <input
                                            type="number" min="0" max="100"
                                            value={formData.kdv}
                                            onChange={e => setFormData({ ...formData, kdv: parseInt(e.target.value) || 0 })}
                                        />
                                    </div>
                                    <div className="pal-mfield pal-modal-full">
                                        <label>{t('label.adres', 'Adres')}</label>
                                        <textarea
                                            placeholder="Proje adresi..."
                                            value={formData.adres}
                                            onChange={e => setFormData({ ...formData, adres: e.target.value })}
                                            maxLength={100}
                                        />
                                    </div>
                                    <div className="pal-mfield pal-modal-full">
                                        <label>{t('proje.logo', 'Proje Logo')}</label>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                        />
                                        <div className="pal-mfield-hint">Sadece JPG veya PNG · Maks. 10 MB</div>
                                    </div>
                                </div>
                            </div>
                            <div className="pal-modal-footer">
                                <button type="button" className="pal-btn-cancel" onClick={handleCloseModal}>{t('btn.iptal', 'İptal')}</button>
                                <button type="submit" className="pal-btn-save" disabled={saving}>
                                    {saving && <span className="pal-spinner" />}
                                    {saving ? 'Kaydediliyor...' : t('btn.kaydet', 'Kaydet')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProjeAyarlariListesi;
